<script lang="ts">
  import type { BlogArchiveBlock, BlogPost } from './types';
  import { handleNavigation } from '@lib/utils/navigation';

  export let block: BlogArchiveBlock;
  export let isFullscreen: boolean = false;

  $: variant = block.variant || 'cards';
  $: columns = block.columns || 3;
  $: showTags = block.showTags !== false;
  $: showDate = block.showDate !== false;
  $: showAuthor = block.showAuthor !== false;
  $: showExcerpt = block.showExcerpt !== false;
  $: posts = block.posts || [];

  function formatDate(dateStr: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  }

  async function navigateToPost(slug: string) {
    // Use parentRoute if available (for generic archives), otherwise default to /blog
    const parentPath = block.parentRoute || 'blog';
    // Clean URL structure: /blog/{slug} or /our-work/{slug}
    const path = `/${parentPath}/${slug}`;
    await handleNavigation(path);
  }
</script>

<div
  class="blog-archive-block"
  class:variant-cards={variant === 'cards'}
  class:variant-list={variant === 'list'}
  style:--columns={columns}
>
  {#if block.title}
    <h2 class="archive-title">{block.title}</h2>
  {/if}

  {#if block.subtitle}
    <p class="archive-subtitle">{block.subtitle}</p>
  {/if}

  {#if posts.length === 0}
    <div class="empty-state">
      <p>No blog posts yet.</p>
    </div>
  {:else if variant === 'cards'}
    <div class="posts-grid">
      {#each posts as post (post.slug)}
        <article
          class="post-card"
          class:featured={post.featured}
          on:click={() => navigateToPost(post.slug)}
          on:keypress={(e) => e.key === 'Enter' && navigateToPost(post.slug)}
          role="button"
          tabindex="0"
        >
          {#if post.image?.url}
            <div class="post-image">
              <img src={post.image.url} alt={post.image.alt || post.title} />
            </div>
          {:else}
            <div class="post-image placeholder">
              <span>📝</span>
            </div>
          {/if}

          <div class="post-content">
            <h3 class="post-title">{post.title}</h3>

            {#if showDate || showAuthor}
              <div class="post-meta">
                {#if showDate && post.date}
                  <time datetime={post.date}>{formatDate(post.date)}</time>
                {/if}
                {#if showAuthor && post.author}
                  <span class="author">by {post.author}</span>
                {/if}
              </div>
            {/if}

            {#if showExcerpt && post.excerpt}
              <p class="post-excerpt">{post.excerpt}</p>
            {/if}

            {#if showTags && post.tags && post.tags.length > 0}
              <div class="post-tags">
                {#each post.tags as tag (tag)}
                  <span class="tag">{tag}</span>
                {/each}
              </div>
            {/if}
          </div>
        </article>
      {/each}
    </div>
  {:else}
    <!-- List variant -->
    <div class="posts-list">
      {#each posts as post (post.slug)}
        <article
          class="post-list-item"
          class:featured={post.featured}
          on:click={() => navigateToPost(post.slug)}
          on:keypress={(e) => e.key === 'Enter' && navigateToPost(post.slug)}
          role="button"
          tabindex="0"
        >
          {#if post.image?.url}
            <div class="post-list-image">
              <img src={post.image.url} alt={post.image.alt || post.title} />
            </div>
          {/if}

          <div class="post-list-content">
            <h3 class="post-title">{post.title}</h3>

            {#if showDate || showAuthor}
              <div class="post-meta">
                {#if showDate && post.date}
                  <time datetime={post.date}>{formatDate(post.date)}</time>
                {/if}
                {#if showAuthor && post.author}
                  <span class="author">by {post.author}</span>
                {/if}
              </div>
            {/if}

            {#if showExcerpt && post.excerpt}
              <p class="post-excerpt">{post.excerpt}</p>
            {/if}

            {#if showTags && post.tags && post.tags.length > 0}
              <div class="post-tags">
                {#each post.tags as tag (tag)}
                  <span class="tag">{tag}</span>
                {/each}
              </div>
            {/if}
          </div>
        </article>
      {/each}
    </div>
  {/if}
</div>

<style lang="postcss">
  .blog-archive-block {
    width: 100%;
    container-type: inline-size;
  }

  .archive-title {
    font-size: 2rem;
    font-weight: 700;
    color: var(--theme-text-color, #1a1a1a);
    margin-bottom: 0.5rem;
  }

  .archive-subtitle {
    font-size: 1.125rem;
    color: var(--theme-text-color, #666);
    opacity: 0.8;
    margin-bottom: 2rem;
  }

  .empty-state {
    text-align: center;
    padding: 4rem 2rem;
    color: var(--theme-text-color, #666);
  }

  /* Cards variant */
  .posts-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 2rem;
  }

  @container (min-width: 900px) {
    .posts-grid {
      grid-template-columns: repeat(var(--columns, 3), 1fr);
    }
  }

  .post-card {
    background: var(--theme-bg-color, #fff);
    border-radius: var(--theme-radius, 0.5rem);
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
  }

  .post-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .post-card.featured {
    border: 2px solid var(--theme-primary-color, #ff1055);
  }

  .post-image {
    width: 100%;
    height: 180px;
    overflow: hidden;
    background: var(--theme-border-color, #f0f0f0);
  }

  .post-image img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .post-image.placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 3rem;
  }

  .post-content {
    padding: 1.25rem;
  }

  .post-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--theme-text-color, #1a1a1a);
    margin: 0 0 0.5rem;
    line-height: 1.3;
  }

  .post-meta {
    display: flex;
    gap: 0.75rem;
    font-size: 0.8125rem;
    color: var(--theme-text-color, #666);
    opacity: 0.7;
    margin-bottom: 0.5rem;
  }

  .post-excerpt {
    color: var(--theme-text-color, #444);
    font-size: 0.875rem;
    line-height: 1.5;
    margin: 0 0 0.75rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .post-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
  }

  .tag {
    font-size: 0.6875rem;
    padding: 0.2rem 0.5rem;
    background: var(--theme-border-color, #f0f0f0);
    border-radius: 0.25rem;
    color: var(--theme-text-color, #666);
  }

  /* List variant */
  .posts-list {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .post-list-item {
    display: flex;
    gap: 1.5rem;
    padding: 1rem;
    background: var(--theme-bg-color, #fff);
    border-radius: var(--theme-radius, 0.5rem);
    cursor: pointer;
    transition: background-color 0.2s;
    border: 1px solid var(--theme-border-color, #e5e7eb);
  }

  .post-list-item:hover {
    background: var(--theme-border-color, #f9fafb);
  }

  .post-list-item.featured {
    border-left: 3px solid var(--theme-primary-color, #ff1055);
  }

  .post-list-image {
    flex-shrink: 0;
    width: 120px;
    height: 90px;
    border-radius: 0.375rem;
    overflow: hidden;
    background: var(--theme-border-color, #f0f0f0);
  }

  .post-list-image img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .post-list-content {
    flex: 1;
    min-width: 0;
  }

  .post-list-content .post-title {
    font-size: 1rem;
    margin-bottom: 0.25rem;
  }

  .post-list-content .post-excerpt {
    -webkit-line-clamp: 1;
  }

  /* Mobile adjustments */
  @container (max-width: 600px) {
    .post-list-item {
      flex-direction: column;
      gap: 1rem;
    }

    .post-list-image {
      width: 100%;
      height: 160px;
    }
  }
</style>
